import { deviationPct, type PeriodWindow } from './budget-deviation-calc';

export interface ApprovedTaskAssignment {
  assignmentId: string;
  userId: string;
  budgetLineId: string;
  projectId: string;
  projectName: string;
  projectCode: string | null;
  customerName: string | null;
  taskName: string;
  taskStatus: string | null;
  completedAt: string | null;
  approvedAt: string | null;
  assignedHours: number;
}

export interface ApprovedTaskTimeEntry {
  assignmentId: string | null;
  userId: string | null;
  budgetLineId: string | null;
  durationSeconds: number | null;
}

export interface ApprovedTaskRow extends ApprovedTaskAssignment {
  loggedHours: number;
  pct: number | null;
}

export interface ApprovedTaskProjectGroup {
  projectId: string;
  projectName: string;
  projectCode: string | null;
  customerName: string | null;
  assignedHours: number;
  loggedHours: number;
  pct: number | null;
  tasks: ApprovedTaskRow[];
}

export interface ApprovedTaskEmployeeRow {
  userId: string;
  taskCount: number;
  assignedHours: number;
  loggedHours: number;
  pct: number | null;
  projects: ApprovedTaskProjectGroup[];
}

export interface ApprovedTaskTeamDeviation {
  taskCount: number;
  assignedHours: number;
  loggedHours: number;
  pct: number | null;
}

export interface ApprovedTaskDeviationResult {
  team: ApprovedTaskTeamDeviation;
  employees: ApprovedTaskEmployeeRow[];
}

function compareWorstFirst(
  a: { pct: number | null },
  b: { pct: number | null },
): number {
  if (a.pct == null) return b.pct == null ? 0 : 1;
  if (b.pct == null) return -1;
  return b.pct - a.pct;
}

export function computeApprovedTaskDeviations(
  assignments: ApprovedTaskAssignment[],
  timeEntries: ApprovedTaskTimeEntry[],
  window: PeriodWindow,
): ApprovedTaskDeviationResult {
  const assignmentCountByUserBudgetLine = new Map<string, number>();
  for (const assignment of assignments) {
    const key = `${assignment.userId}|${assignment.budgetLineId}`;
    assignmentCountByUserBudgetLine.set(
      key,
      (assignmentCountByUserBudgetLine.get(key) ?? 0) + 1,
    );
  }

  const loggedByAssignment = new Map<string, number>();
  const orphanLoggedByUserBudgetLine = new Map<string, number>();
  for (const entry of timeEntries) {
    if (!entry.budgetLineId) continue;
    const hours = Number(entry.durationSeconds ?? 0) / 3600;
    if (entry.assignmentId) {
      loggedByAssignment.set(
        entry.assignmentId,
        (loggedByAssignment.get(entry.assignmentId) ?? 0) + hours,
      );
      continue;
    }
    if (!entry.userId) continue;
    const key = `${entry.userId}|${entry.budgetLineId}`;
    orphanLoggedByUserBudgetLine.set(
      key,
      (orphanLoggedByUserBudgetLine.get(key) ?? 0) + hours,
    );
  }

  const tasks = assignments
    .filter(
      (item) =>
        item.taskStatus === 'approved' &&
        item.completedAt != null &&
        item.completedAt >= window.start &&
        item.completedAt < window.end,
    )
    .map<ApprovedTaskRow>((item) => {
      const pairKey = `${item.userId}|${item.budgetLineId}`;
      const directHours = loggedByAssignment.get(item.assignmentId) ?? 0;
      const orphanHours =
        assignmentCountByUserBudgetLine.get(pairKey) === 1
          ? (orphanLoggedByUserBudgetLine.get(pairKey) ?? 0)
          : 0;
      const loggedHours = directHours + orphanHours;
      return {
        ...item,
        loggedHours,
        pct: deviationPct(loggedHours, item.assignedHours),
      };
    });

  const tasksByUser = new Map<string, ApprovedTaskRow[]>();
  for (const task of tasks) {
    const userTasks = tasksByUser.get(task.userId) ?? [];
    userTasks.push(task);
    tasksByUser.set(task.userId, userTasks);
  }

  const employees: ApprovedTaskEmployeeRow[] = [];
  for (const [userId, userTasks] of tasksByUser) {
    const tasksByProject = new Map<string, ApprovedTaskRow[]>();
    for (const task of userTasks) {
      const projectTasks = tasksByProject.get(task.projectId) ?? [];
      projectTasks.push(task);
      tasksByProject.set(task.projectId, projectTasks);
    }

    const projects = [...tasksByProject.values()]
      .map<ApprovedTaskProjectGroup>((projectTasks) => {
        const first = projectTasks[0];
        const assignedHours = projectTasks.reduce((sum, task) => sum + task.assignedHours, 0);
        const loggedHours = projectTasks.reduce((sum, task) => sum + task.loggedHours, 0);
        return {
          projectId: first.projectId,
          projectName: first.projectName,
          projectCode: first.projectCode,
          customerName: first.customerName,
          assignedHours,
          loggedHours,
          pct: deviationPct(loggedHours, assignedHours),
          tasks: projectTasks.sort(
            (a, b) => compareWorstFirst(a, b) || a.assignmentId.localeCompare(b.assignmentId),
          ),
        };
      })
      .sort(
        (a, b) => compareWorstFirst(a, b) || a.projectId.localeCompare(b.projectId),
      );

    const assignedHours = userTasks.reduce((sum, task) => sum + task.assignedHours, 0);
    const loggedHours = userTasks.reduce((sum, task) => sum + task.loggedHours, 0);
    employees.push({
      userId,
      taskCount: userTasks.length,
      assignedHours,
      loggedHours,
      pct: deviationPct(loggedHours, assignedHours),
      projects,
    });
  }

  const assignedHours = tasks.reduce((sum, task) => sum + task.assignedHours, 0);
  const loggedHours = tasks.reduce((sum, task) => sum + task.loggedHours, 0);
  return {
    team: {
      taskCount: tasks.length,
      assignedHours,
      loggedHours,
      pct: deviationPct(loggedHours, assignedHours),
    },
    employees,
  };
}
